/* =========================================================
   FOOTER CELINA PREMIUM
   footer-celina.js
   ========================================================= */

(() => {
  'use strict';

  const SELECTOR = '[data-cc-footer]';

  class CelinaFooter {
    constructor(root) {
      this.root = root;
      this.revealItems = [...root.querySelectorAll('.cc-reveal')];
      this.groups = [...root.querySelectorAll('[data-footer-group]')];
      this.localizationSelects = [...root.querySelectorAll('[data-localization-select]')];
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileQuery = window.matchMedia('(max-width: 700px)');

      this.revealObserver = null;

      this.init();
    }

    init() {
      this.initReveal();
      this.initMobileMenus();
      this.initLocalization();
      this.initPointerEffects();
      this.initShopifyEditor();
    }

    /* -------------------------------------------------------
       ENTRADAS PREMIUM
       ------------------------------------------------------- */

    initReveal() {
      if (!this.revealItems.length) return;

      if (this.motionQuery.matches || !('IntersectionObserver' in window)) {
        this.revealItems.forEach((item) => item.classList.add('is-visible'));
        return;
      }

      this.revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        },
        {
          threshold: 0.12,
          rootMargin: '0px 0px -5% 0px'
        }
      );

      this.revealItems.forEach((item) => {
        this.revealObserver.observe(item);
      });
    }

    /* -------------------------------------------------------
       ACORDEONES EN MÓVIL
       ------------------------------------------------------- */

    initMobileMenus() {
      if (!this.groups.length) return;

      this.groups.forEach((group) => {
        const button = group.querySelector('.cc-footer__column-toggle');
        const panel = group.querySelector('[data-footer-panel]');

        if (!button || !panel) return;

        button.addEventListener('click', () => {
          if (!this.mobileQuery.matches) return;

          const willOpen = !group.classList.contains('is-open');

          this.groups.forEach((otherGroup) => {
            if (otherGroup === group) return;

            otherGroup.classList.remove('is-open');

            const otherButton = otherGroup.querySelector('.cc-footer__column-toggle');
            if (otherButton) {
              otherButton.setAttribute('aria-expanded', 'false');
            }
          });

          group.classList.toggle('is-open', willOpen);
          button.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        });
      });

      const syncMenus = () => {
        if (!this.mobileQuery.matches) {
          this.groups.forEach((group) => {
            group.classList.remove('is-open');

            const button = group.querySelector('.cc-footer__column-toggle');
            if (button) {
              button.setAttribute('aria-expanded', 'false');
            }
          });
        }
      };

      if (typeof this.mobileQuery.addEventListener === 'function') {
        this.mobileQuery.addEventListener('change', syncMenus);
      } else if (typeof this.mobileQuery.addListener === 'function') {
        this.mobileQuery.addListener(syncMenus);
      }
    }

    /* -------------------------------------------------------
       PAÍS / IDIOMA
       ------------------------------------------------------- */

    initLocalization() {
      this.localizationSelects.forEach((select) => {
        select.addEventListener('change', () => {
          const form = select.closest('form');
          if (!form) return;
          form.submit();
        });
      });
    }

    /* -------------------------------------------------------
       MICRO INTERACCIÓN DE PUNTERO
       ------------------------------------------------------- */

    initPointerEffects() {
      if (
        this.motionQuery.matches ||
        !window.matchMedia('(hover: hover) and (pointer: fine)').matches
      ) {
        return;
      }

      const interactiveItems = [
        ...this.root.querySelectorAll('.cc-footer__social'),
        ...this.root.querySelectorAll('.cc-footer__payment-icon'),
        ...this.root.querySelectorAll('.cc-footer__trust-item')
      ];

      interactiveItems.forEach((item) => {
        item.addEventListener('pointermove', (event) => {
          const rect = item.getBoundingClientRect();

          if (!rect.width || !rect.height) return;

          const x = (event.clientX - rect.left) / rect.width;
          const y = (event.clientY - rect.top) / rect.height;

          const rotateY = (x - 0.5) * 4;
          const rotateX = (0.5 - y) * 4;

          item.style.setProperty('--cc-rotate-x', `${rotateX.toFixed(2)}deg`);
          item.style.setProperty('--cc-rotate-y', `${rotateY.toFixed(2)}deg`);
        });

        item.addEventListener('pointerleave', () => {
          item.style.setProperty('--cc-rotate-x', '0deg');
          item.style.setProperty('--cc-rotate-y', '0deg');
        });
      });
    }

    /* -------------------------------------------------------
       EDITOR DE SHOPIFY
       ------------------------------------------------------- */

    initShopifyEditor() {
      if (!window.Shopify || !Shopify.designMode) return;

      document.addEventListener('shopify:block:select', (event) => {
        if (!this.root.contains(event.target)) return;

        const group = event.target.closest('[data-footer-group]');

        if (group && this.mobileQuery.matches) {
          const button = group.querySelector('.cc-footer__column-toggle');

          group.classList.add('is-open');

          if (button) {
            button.setAttribute('aria-expanded', 'true');
          }
        }

        const reveal = event.target.closest('.cc-reveal');
        if (reveal) {
          reveal.classList.add('is-visible');
        }
      });
    }

    destroy() {
      if (this.revealObserver) {
        this.revealObserver.disconnect();
      }
    }
  }

  const instances = new WeakMap();

  const initFooter = (root) => {
    if (!root || instances.has(root)) return;

    const instance = new CelinaFooter(root);
    instances.set(root, instance);
  };

  const initAll = (scope = document) => {
    if (scope.matches && scope.matches(SELECTOR)) {
      initFooter(scope);
    }

    scope.querySelectorAll?.(SELECTOR).forEach(initFooter);
  };

  const boot = () => {
    initAll(document);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(SELECTOR)
      ? event.target
      : event.target.querySelector?.(SELECTOR);

    if (!root) return;

    const instance = instances.get(root);

    if (instance) {
      instance.destroy();
      instances.delete(root);
    }
  });
})();